import { Component, OnInit, Optional, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA  } from '@angular/material/dialog';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { TranslateService } from '@ngx-translate/core';
import { BankWrapperModalComponent } from '@shared/bank-wrapper-modal/bank-wrapper-modal.component';
import svgIconList from 'assets/icons.json';
import { throwError  } from 'rxjs';
import { catchError, delay, map, tap } from 'rxjs/operators';
import { CreditCardHttpService } from '@core/services/credit-card-http.service';
import Swal, { SweetAlertCustomClass } from 'sweetalert2';


@Component({
  selector: 'app-credit-card-modal',
  templateUrl: './credit-card-modal.component.html',
  styleUrls: ['./credit-card-modal.component.scss']
})
export class CreditCardModalComponent implements OnInit {
  svg: any = svgIconList;
  form: FormGroup;

  cards = [];

  checkValidity = false;
  disabledButton = false;

  userData = JSON.parse(localStorage.getItem('user_data'));
  userName = localStorage.getItem('profile') === null || localStorage.getItem('profile') === '' ? JSON.parse(localStorage.getItem('user_data')).name : localStorage.getItem('profile');

  isSuccess = this.creditCardHttpService.isSuccess;
  messages$ = this.creditCardHttpService.messages$;

  constructor(
    public dialogRef: MatDialogRef<BankWrapperModalComponent>,
    private creditCardHttpService: CreditCardHttpService,
    private translateService: TranslateService,
    public loadingBar: LoadingBarService,
    private dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: {
      isDialog: boolean,
      card: any,
    },
  ) 
  { 
    
  }

  async ngOnInit() {
    this.formInit();
    this.getCards();
    if( this.data.isDialog ) {
      this.setupEditCard();
    }

    if (!this.userData.name) {
      this.form.get('card_holder_name').setValidators([Validators.required]);
    } else {
      this.form.get('card_holder_name').clearValidators();
    }

    this.form.get('card_holder_name').updateValueAndValidity();
  }

  getCards() {
    if( !this.data.isDialog ) {
      this.creditCardHttpService.getWithQuery('?status=1').pipe(
        tap(res => {
          this.cards = res;
        })
      ).subscribe();
    }  
  }

  onSubmit() {
    this.form.markAllAsTouched();
    
    if( this.form.valid ) {
      this.checkValidity = true;
      this.loadingBar.start();
      this.disabledButton = true;

      const data = {
        ...this.form.value,
      };
      Object.keys(data).forEach((key) => (data[key] == null || data[key] === '') && delete data[key]);

      if( this.data.isDialog ) {
        // Update Card
        this.creditCardHttpService.update(this.data.card.id, data).pipe(
          catchError(err => {
            this.loadingBar.complete();
            this.checkValidity = false;
            this.disabledButton = false;
            return throwError(err);
          })
        ).subscribe(() => {
          this.loadingBar.complete();
          this.checkValidity = false;
          this.disabledButton = false;
          this.isSuccess = this.creditCardHttpService.isSuccess;
          this.onReset();
          this.onCloseDialog(true);
          this.getCards();
        });
      } else {
        // Create Card
        this.creditCardHttpService.create(data).pipe(
          catchError(err => {
            this.loadingBar.complete();
            this.checkValidity = false;
            this.disabledButton = false;
            return throwError(err);
          })
        ).subscribe(() => {
          this.loadingBar.complete();
          this.checkValidity = false;
          this.disabledButton = false;
          this.isSuccess = this.creditCardHttpService.isSuccess;
          this.onReset();
          this.getCards();
        });
      }
    }
  }

  onRemove() {
    if( this.data.isDialog ) {
      const message = this.translateService.instant('Are you sure you want to remove this card?');
      const cardDetail = this.data.card.card_brand_name + ' &#x2022;&#x2022;&#x2022;&#x2022; &#x2022;&#x2022;&#x2022;&#x2022; &#x2022;&#x2022;&#x2022;&#x2022; ' + this.data.card.last_four + ' ' + this.data.card.expiry_month + '/' + this.data.card.expiry_year;
      Swal.fire({
        html: '<div class="msg-icon-info">' + this.svg.dangerIcon + '</div>' +
              '<div class="text-center m-t-20">' +
                message +
              '</div>' +
              '<div class="text-center m-t-20">' + cardDetail + '</div>',
        confirmButtonText: this.translateService.instant('YES'),
        showDenyButton: true,
        showConfirmButton: true,
        denyButtonText: this.translateService.instant('NO'),
        reverseButtons: true,
        customClass: {
          denyButton: 'deny-button-blue',
          confirmButton: 'confirm-button',
        }
      }).then(result => {
        if (result.isConfirmed) {
          this.creditCardHttpService.delete(this.data.card.id).pipe(
            catchError(err => {
              this.loadingBar.complete();
              this.checkValidity = false;
              this.disabledButton = false;
              return throwError(err);
            })
          ).subscribe(() => {
            this.loadingBar.complete();
            this.checkValidity = false;
            this.disabledButton = false;
            this.isSuccess = this.creditCardHttpService.isSuccess;
            this.onReset();
            this.onCloseDialog(true);
            this.getCards();
          });
        }
      });
    }
  }

  onReset() {
    this.form.reset();
  }

  onEdit(card) {
    this.creditCardHttpService.getById(card.id).pipe(
      tap(res => {
        const dialogRef = this.dialog.open(CreditCardModalComponent, {
          width: '800px',
          data: { isDialog: true, card: res }
        });

        dialogRef.afterClosed().subscribe((result) => {
          if(result === true) {
            this.getCards();
          }
        });
      })
    ).subscribe();
  }

  formInit() {
    this.form = new FormGroup({
      card_holder_name: new FormControl(this.userName, []),
      card_number: new FormControl(null, [
        Validators.required,
        this.cardNumberValidator,
      ]),
      expiry_date: new FormControl(null, [
        Validators.required,
        Validators.pattern(/^(0[1-9]|1[0-2])\/\d{2}$/), // MM/YY format
        this.expiryDateValidator()
      ])
    });
    this.setupCardInputFormatting();
  }

  setupEditCard() {
    if( this.data.isDialog && this.data.card !== null ) {
      this.form.patchValue({
        card_number: this.data.card.decrypted_card_number,
        expiry_date: this.data.card.expiry_month + '/' + this.data.card.expiry_year
      });

    }
  }

  expiryDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const [month, year] = value.split('/').map(Number);
      if (!month || !year) return { invalidExpiry: true };

      const now = new Date();
      const currentYear = now.getFullYear() % 100;
      const currentMonth = now.getMonth() + 1;

      if (year < currentYear || (year === currentYear && month < currentMonth)) {
        return { expired: true };
      }

      return null;
    };
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.charCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault(); // prevent non-numeric input
    }
  }

  onCloseDialog(refresh?: any) {
    this.dialogRef.close(refresh);
  }

  private setupCardInputFormatting(): void {
    // Format card number input (add spaces every 4 digits)
    this.form.get('card_number')?.valueChanges.subscribe(value => {
      if (value) {
        const cleanValue = value.replace(/\D/g, '');
        const formattedValue = cleanValue.replace(/(\d{4})(?=\d)/g, '$1 ');
        if (formattedValue !== value) {
          this.form.get('card_number')?.setValue(formattedValue, { emitEvent: false });
        }
      }
    });

    // Format expiry date input (MM/YY format with 2-digit year limit)
    this.form.get('expiry_date')?.valueChanges.subscribe(value => {
      if (value) {
        // Remove any non-digit characters
        const cleanValue = value.replace(/\D/g, '');

        if (cleanValue.length > 0) {
          let formattedValue = '';

          if (cleanValue.length <= 2) {
            // Just month digits
            formattedValue = cleanValue;
          } else if (cleanValue.length <= 4) {
            // Month + year digits
            formattedValue = cleanValue.substring(0, 2) + '/' + cleanValue.substring(2, 4);
          } else {
            // More than 4 digits - use the latest 4 digits
            const latest4Digits = cleanValue.slice(-4);
            formattedValue = latest4Digits.substring(0, 2) + '/' + latest4Digits.substring(2, 4);
          }

          if (formattedValue !== value) {
            this.form.get('expiry_date')?.setValue(formattedValue, { emitEvent: false });
          }
        }
      }
    });
  }

  cardNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    // Remove any spaces or non-digit characters for validation
    const cleanValue = value.replace(/\D/g, '');

    // Check if it contains only digits and is between 13-19 characters
    if (!/^\d{13,19}$/.test(cleanValue)) {
      return { invalidCardNumber: true };
    }

    return null;
  }
}